import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "antd";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import useGetListByUserKey from "@base/modules/user/profile/hooks/useGetListByUserKey";
import { IPatientRecordData } from "@base/modules/user/profile/profile.interface";
import { IHealthInsuranceCard } from "@base/modules/patientInsurance/healthInsurance.interface";
import patientInsuranceService from "@base/modules/patientInsurance/patientInsuranceService";
import HealthInsuranceCard from "@components/User/Profile/InsuranceCards/HealthInsuranceCard";
import LifeInsuranceSection from "./LifeInsuranceSection";

/**
 * "Insurance": the insurance kept on one of the account's records. The record comes first - the
 * primary one by default - then its national health insurance (BHYT) and its health insurances.
 */
export default function Insurance() {
  //! state
  const { t, i18n } = useTranslation();
  const { isLoading: isLoadingRecords, data } = useGetListByUserKey();
  const records: IPatientRecordData[] = useMemo(() => data?.data ?? [], [data]);

  const [recordCode, setRecordCode] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>("life");
  const [healthCard, setHealthCard] = useState<IHealthInsuranceCard>();
  const [isLoadingHealth, setIsLoadingHealth] = useState<boolean>(false);

  const patientRecord = records.find((record) => record.patient_record?.code === recordCode)?.patient_record;
  const lang = i18n.resolvedLanguage ?? "vi";

  //! function
  // The list is sorted with the primary record first.
  useEffect(() => {
    if (!recordCode && records.length) {
      setRecordCode(records[0].patient_record?.code);
    }
  }, [records, recordCode]);

  const getHealthCard = useCallback(async () => {
    if (!recordCode) {
      return;
    }

    try {
      setIsLoadingHealth(true);
      const resp = await patientInsuranceService.getHealthByPatientCode(recordCode, lang);
      setHealthCard(resp.data?.name ? resp.data : undefined);
    } catch (error) {
      setHealthCard(undefined);
    } finally {
      setIsLoadingHealth(false);
    }
  }, [recordCode, lang]);

  useEffect(() => {
    getHealthCard();
  }, [getHealthCard]);

  //! render
  const renderHealth = () => (
    <div className="min-h-[400px] relative flex flex-col gap-4">
      <CommonStyles.SpinnerUI isSpinning={isLoadingHealth} />
      <span className="text-common-subtext text-sm font-normal font-['Inter']">{t("insurance.healthDescription")}</span>

      {healthCard ? <HealthInsuranceCard card={healthCard} /> : null}

      {!isLoadingHealth && !healthCard ? (
        <div className="w-full min-h-[320px] flex flex-col justify-center items-center gap-6">
          <img src={CommonImages.EmptyRecordList} alt="" />
          <div className="text-common-maintext text-base font-semibold font-['Inter']">{t("insurance.healthEmpty")}</div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="p-5">
      <div className="flex flex-col min-[450px]:flex-row min-[450px]:justify-between min-[450px]:items-center gap-3 min-h-8">
        <div className="text-common-primary text-base font-bold font-['Inter']">{t("insurance.title")}</div>

        {records.length ? (
          <CommonStyles.SelectUI
            rootClass="max-sm:w-full"
            className="w-72 max-sm:w-full"
            variant="filled"
            value={recordCode}
            options={records.map((record) => ({ label: record.patient_record?.patient_name, value: record.patient_record?.code }))}
            onChange={(value: string) => setRecordCode(value)}
          />
        ) : null}
      </div>

      <Divider />

      {isLoadingRecords ? <CommonStyles.LoadingUI /> : null}

      {!isLoadingRecords && !records.length ? (
        <div className="w-full min-h-[400px] flex flex-col justify-center items-center gap-6">
          <img src={CommonImages.EmptyRecordList} alt="" />
          <div className="text-common-maintext text-base font-semibold font-['Inter']">{t("user.profile.recordNotFound")}</div>
        </div>
      ) : null}

      {patientRecord ? (
        <CommonStyles.TabsUI
          className="w-full"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "life", label: t("insurance.lifeTab"), children: <LifeInsuranceSection key={recordCode} patientRecord={patientRecord} /> },
            { key: "health", label: t("insurance.healthTab"), children: renderHealth() },
          ]}
        />
      ) : null}
    </div>
  );
}
